/**
 * Pacote 3 · P3-12 (D2 aprovada): concluir a próxima ação da Oportunidade — na Ficha, em Tarefas e, por opção
 * explícita, no Registrar atividade. Ao concluir, escolher a nova próxima ação ou "Sem próxima ação" (exceção, com
 * justificativa no Histórico). Concluir não muda o estágio nem registra qualificação comercial.
 */
import { useState } from 'react';
import { api } from '../api.ts';
import { hojeIso } from './CampoData.tsx';
import { Ajuda, Estado, Modal } from './ui.tsx';

export interface Conclusao {
  modo: 'nova' | 'sem';
  nova: { descricao: string; data: string; responsavel_id: string };
  justificativa: string;
}
export const conclusaoInicial = (responsavelId: string): Conclusao => ({ modo: 'nova', nova: { descricao: '', data: hojeIso(), responsavel_id: responsavelId }, justificativa: '' });
export const faltasConclusao = (v: Conclusao) =>
  (v.modo === 'nova'
    ? [!v.nova.descricao.trim() && 'Informe a nova próxima ação.', !v.nova.data && 'Informe a data da nova próxima ação.', v.nova.data && v.nova.data < hojeIso() && 'A nova próxima ação precisa ter data de hoje em diante.']
    : [v.justificativa.trim().length < 4 && 'Sem próxima ação: informe a justificativa.']
  ).filter(Boolean) as string[];
export const corpoConclusao = (v: Conclusao) => (v.modo === 'nova' ? { nova: { descricao: v.nova.descricao.trim(), data: v.nova.data, responsavel_id: v.nova.responsavel_id } } : { nova: null, justificativa: v.justificativa.trim() });

export function CamposConclusao({ v, setV, responsaveis, teste = 'conclusao' }: { v: Conclusao; setV: (x: Conclusao) => void; responsaveis: { id: string; nome: string }[]; teste?: string }) {
  return (
    <div className="campos-conclusao" data-teste={teste}>
      <label className="opcao">
        <input type="radio" name={`${teste}-modo`} checked={v.modo === 'nova'} onChange={() => setV({ ...v, modo: 'nova' })} data-teste={`${teste}-nova`} /> <strong>Nova próxima ação</strong>
      </label>
      {v.modo === 'nova' && (
        <div className="grade-3 recuo-conclusao">
          <label className="campo">
            Descrição *
            <input value={v.nova.descricao} onChange={(e) => setV({ ...v, nova: { ...v.nova, descricao: e.target.value } })} maxLength={300} data-teste={`${teste}-descricao`} />
          </label>
          <label className="campo">
            Data *
            <input type="date" value={v.nova.data} min={hojeIso()} onChange={(e) => setV({ ...v, nova: { ...v.nova, data: e.target.value } })} data-teste={`${teste}-data`} />
          </label>
          <label className="campo">
            Responsável *
            <select value={v.nova.responsavel_id} onChange={(e) => setV({ ...v, nova: { ...v.nova, responsavel_id: e.target.value } })} data-teste={`${teste}-responsavel`}>
              {responsaveis.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.nome}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      <label className="opcao">
        <input type="radio" name={`${teste}-modo`} checked={v.modo === 'sem'} onChange={() => setV({ ...v, modo: 'sem' })} data-teste={`${teste}-sem`} /> <strong>Sem próxima ação</strong>
      </label>
      {v.modo === 'sem' && (
        <label className="campo recuo-conclusao">
          <span>
            Justificativa *<Ajuda titulo="Sem próxima ação">A próxima ação é obrigatória por padrão. Ficar sem ela é uma exceção registrada no Histórico com a justificativa.</Ajuda>
          </span>
          <input value={v.justificativa} onChange={(e) => setV({ ...v, justificativa: e.target.value })} maxLength={1000} data-teste={`${teste}-justificativa`} />
        </label>
      )}
    </div>
  );
}

export function ModalConcluirProximaAcao({ oportunidade, responsaveis, aoFechar, aoSalvar }: { oportunidade: { id: string; atualizado_em?: string; proxima_acao: { descricao: string; responsavel: { id: string } } }; responsaveis: { id: string; nome: string }[]; aoFechar: () => void; aoSalvar: () => void }) {
  const [v, setV] = useState<Conclusao>(conclusaoInicial(oportunidade.proxima_acao.responsavel.id));
  const [tentou, setTentou] = useState(false);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const faltam = faltasConclusao(v);
  const salvar = async () => {
    setTentou(true);
    if (faltam.length || salvando) return;
    setSalvando(true);
    setErro('');
    try {
      await api.post(`/api/oportunidades/${oportunidade.id}/proxima-acao/concluir`, { ...corpoConclusao(v), ...(oportunidade.atualizado_em ? { atualizado_em: oportunidade.atualizado_em } : {}) });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Modal
      titulo={`Concluir “${oportunidade.proxima_acao.descricao}”`}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" disabled={salvando} aria-busy={salvando} onClick={salvar} data-teste="confirmar-conclusao">
            Concluir
          </button>
        </>
      }
    >
      {erro && (
        <p className="aviso aviso-erro" role="alert" data-teste="erro-conclusao">
          {erro}
        </p>
      )}
      <p className="texto-secundario sem-margem">O estágio não muda.</p>
      <CamposConclusao v={v} setV={setV} responsaveis={responsaveis} />
      {tentou && faltam.length > 0 && <Estado tipo="bloqueado" titulo="Falta completar" itens={faltam} teste="faltam-conclusao" />}
    </Modal>
  );
}
